import { useEffect } from 'react';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  collection,
} from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';

function isStale(timestamp) {
  const lastModified = timestamp?.toDate?.() || (timestamp ? new Date(timestamp) : null);
  if (!lastModified) return true;

  const now = new Date();
  const monthsSince =
    (now.getFullYear() - lastModified.getFullYear()) * 12 +
    (now.getMonth() - lastModified.getMonth());

  return monthsSince >= 12;
}

export default function ProfileDetailsReminder() {
  const { user } = useAuth();
  const userId = user?.uid || '';
  const userCreationTime = user?.metadata?.creationTime || null;

  useEffect(() => {
    const checkProfileDetailsStale = async () => {
      if (!userId) return;

      const [userSnap, privateSnap] = await Promise.all([
        getDoc(doc(db, 'users', userId)),
        getDoc(doc(db, 'privateUsers', userId)),
      ]);
      if (!userSnap.exists() || !privateSnap.exists()) return;

      const userData = userSnap.data() || {};
      const privateData = privateSnap.data() || {};
      const createdAt = privateData?.createdAt || userData?.joinDate || userCreationTime;
      const syncRequiresContactDetails = privateData?.autoSyncFamilyEmergencyContacts === true;
      const hasCoreDetails = Boolean(
        String(userData?.firstName || '').trim() &&
        String(userData?.lastName || '').trim() &&
        (!syncRequiresContactDetails || String(privateData?.phone || '').trim()) &&
        (!syncRequiresContactDetails || String(privateData?.address || '').trim()) &&
        String(privateData?.dob || '').trim()
      );
      const existingQuery = query(
        collection(db, 'notifications', userId, 'items'),
        where('type', '==', 'reminder-profile-check')
      );
      const existingSnap = await getDocs(existingQuery);
      const profileIsStale = isStale(privateData?.profileLastModified || createdAt);
      const reminderMessage = !hasCoreDetails
        ? syncRequiresContactDetails
          ? 'We need a few more details. Please add your phone number, address, and personal information.'
          : 'We need a few more details. Please check your personal information.'
        : profileIsStale
          ? syncRequiresContactDetails
            ? 'It has been a while since you reviewed your profile details. Please check your phone number, address, and personal information.'
            : 'It has been a while since you reviewed your profile details. Please check your personal information.'
          : '';

      if (!reminderMessage) {
        if (!existingSnap.empty) {
          await Promise.all(existingSnap.docs.map((snap) => deleteDoc(snap.ref).catch(() => {})));
        }
        return;
      }

      if (!existingSnap.empty) {
        await Promise.all(existingSnap.docs.map((snap) => setDoc(snap.ref, {
          message: reminderMessage,
          actionUrl: '/profile',
          notificationCategory: 'family_profile',
        }, { merge: true }).catch(() => {})));
        return;
      }

      const notificationRef = doc(db, 'notifications', userId, 'items', crypto.randomUUID());
      await setDoc(notificationRef, {
        type: 'reminder-profile-check',
        notificationCategory: 'family_profile',
        message: reminderMessage,
        actionUrl: '/profile',
        read: false,
        timestamp: serverTimestamp(),
        emailed: false,
      });
    };

    checkProfileDetailsStale();
  }, [userCreationTime, userId]);

  return null;
}
